import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from 'notistack';
import useApi from "hooks/useApi";
// MUI
import { Box, Table, IconButton, TableBody, TableCell, TableContainer, TableHead, TableSortLabel, TablePagination, TableRow, CircularProgress, Paper, Typography, TextField, Autocomplete } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import { makeStyles } from "@mui/styles";
import { visuallyHidden } from "@mui/utils";
//elements
import PropTypes from "prop-types";
import { permanentInfo } from "../../data/permanentInfo";
//api
import { updatePupilsList, rolesMailSender } from "api/api";
import { GreenButton } from "Components/Common/NotInPilotModal";

const useStyles = makeStyles(() => ({
    sortTitle: {
        "&:hover": {
            color: "black !important",
        },
        "& .MuiTableSortLabel-icon": {
            color: "#3a7735 !important",
        },
    },
}));

function EnhancedTableHead({ order, orderBy, onRequestSort }) {
    const classes = useStyles();
    const { t } = useTranslation();

    const headCells = [
        { id: "PupilID", label: t("id"), toSort: false },
        { id: "Pupil_FirstName", label: t("pupilName"), toSort: false },
        { id: "Class", label: t("class"), toSort: false },
        { id: "Phone1", label: t("myPhone"), toSort: false },
        { id: "Role", label: t("pupilRole"), toSort: false }
    ];

    const createSortHandler = (property) => (event) => {
        if (headCells.find(cell => cell.id === property)?.toSort) {
            onRequestSort(event, property);
        }
    };

    return (
        <TableHead>
            <TableRow>
                <TableCell padding="checkbox" />
                {headCells.map((headCell) => (
                    <TableCell
                        sx={{ fontWeight: "bold", fontSize: "1.1rem" }}
                        key={headCell.id}
                        align="center"
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={headCell.toSort && orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : "asc"}
                            onClick={createSortHandler(headCell.id)}
                            hideSortIcon={!headCell.toSort}
                            className={classes.sortTitle}
                        >
                            {headCell.label}
                            {orderBy === headCell.id && (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === "desc" ? "sorted descending" : "sorted ascending"}
                                </Box>
                            )}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    order: PropTypes.oneOf(["asc", "desc"]).isRequired,
    orderBy: PropTypes.string.isRequired,
    onRequestSort: PropTypes.func.isRequired,
};

export default function PupilsRolesTable({ rows, pupilsUpdateRoles, setPupilsUpdateRoles, pupilRolesList, tribe, user }) {
    const { t } = useTranslation();
    const updatePupils = useApi(updatePupilsList);
    const sendRolesMail = useApi(rolesMailSender);
    const { enqueueSnackbar } = useSnackbar();
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const [pupilsToUpdate, setPupilsToUpdate] = useState([]);
    const [editModeRows, setEditModeRows] = useState([]);
    const [customRoles, setCustomRoles] = useState({});
    const [customRolesText, setCustomRolesText] = useState({});
    const [customRolesArray, setCustomRolesArray] = useState([]);
    const [openedRows, setOpenedRows] = useState([]);

    const visibleRows = Array.isArray(rows) ? rows : [];

    const isValidIsraeliPhone = (phone) => /^05[0-9]{8}$/.test(phone);

    const canSave = pupilsToUpdate.every(pupil => {
        const hasValidPhone = isValidIsraeliPhone(pupil.Phone1);
        const role = pupil.PupilRole;
        const hasRoleCode = role && role.RoleCode;

        const isOther = role?.RoleCode === "90";
        const hasCustomText = !isOther || (customRolesText[pupil.StudentCardCode]?.trim().length >= 2);

        return hasValidPhone && hasRoleCode && hasCustomText;
    });

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const toggleEditMode = (studentCardCode) => {
        setEditModeRows(prev => {
            const isOpen = prev.includes(studentCardCode);
            if (!isOpen) {
                setOpenedRows(prevOpened =>
                    prevOpened.includes(studentCardCode) ? prevOpened : [...prevOpened, studentCardCode]
                );
            }
            return isOpen
                ? prev.filter(id => id !== studentCardCode)
                : [...prev, studentCardCode];
        });
    };

    const handleEdit = (studentCardCode, field, value) => {
        setPupilsToUpdate(prev => {
            const existing = prev.find(p => p.StudentCardCode === studentCardCode);
            const updatedValue = value;

            const updatedList = existing
                ? prev.map(p =>
                    p.StudentCardCode === studentCardCode
                        ? { ...p, [field]: updatedValue }
                        : p
                )
                : [...prev, { ...paginatedRows.find(r => r.StudentCardCode === studentCardCode), [field]: updatedValue }];

            return updatedList;
        });
    };

    const saveManyPupils = async () => {
        try {
            const data = pupilsToUpdate.map((child) => {
                const roleCode = child.PupilRole.RoleCode;
                const roleName = child.PupilRole.RoleCode === "90"
                    ? child.PupilRole.RoleName || customRoles[child.StudentCardCode]
                    : child.PupilRole.RoleName;

                return {
                    CardCode: child.StudentCardCode,
                    Group: child.Groups,
                    Troop: child.Troops,
                    School: child.School,
                    Phone: child.Phone1,
                    Role: roleCode    // ✅ send Role code to API
                };
            });

            const result = await updatePupils.requestPromise(data);
            if (customRolesArray.length) {
                const mailDetails = {
                    customRoles: customRolesArray,
                    tribeName: tribe.UnitName,
                    tribeCode: tribe.UnitCode,
                    userEmail: user.email,
                }
                const sendMailToSupport = await sendRolesMail.requestPromise(mailDetails);
                if (sendMailToSupport.statusCode === 202) {
                    enqueueSnackbar(`${t("mailSentToSupport")}`, { variant: "success" });
                }
            }

            if (result) {
                setPupilsUpdateRoles(pupilsUpdateRoles + 1);
                setPupilsToUpdate([]);
                setEditModeRows([]);
                enqueueSnackbar(`${t("updatePupilsSuccesfully")}`, { variant: "success" });
            }
        } catch (error) {
            enqueueSnackbar(`${t("errorOccuredText")}`, { variant: "error" });
            console.error(error);
        }
    };

    const paginatedRows = visibleRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const getLiveFieldValue = (studentCardCode, field, fallback) => {
        const edited = pupilsToUpdate.find(p => p.StudentCardCode === studentCardCode);
        const value = edited?.[field];
        if (value !== undefined) return value;

        // ✅ Normalize Role/PupilRole to full object
        if (field === "PupilRole" || field === "Role") {
            if (typeof fallback === "string") {
                const matched = pupilRolesList.find(r => r.RoleCode === fallback);
                return matched || null;
            }
            if (fallback?.RoleCode && !fallback.RoleName) {
                const matched = pupilRolesList.find(r => r.RoleCode === fallback.RoleCode);
                return matched || null;
            }
        }

        return fallback || null;
    };

    return (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <Paper sx={{ width: "90%", m: 2, boxShadow: "none" }}>
                <Typography>{`${t("updatePupilsDetails")}, ${t("updatePupilsDetails1")}`}</Typography>
                <Typography>*{t("updatePupilsDetails2")}</Typography>
                <GreenButton onClick={saveManyPupils} disabled={!canSave || pupilsToUpdate.length === 0 || updatePupils.loading}>
                    {t("save")}
                    {updatePupils.loading && <CircularProgress size={25} />}
                </GreenButton>
                <TableContainer>
                    <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="medium">
                        <EnhancedTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
                        <TableBody>
                            {paginatedRows.map((row) => {
                                return (
                                    <TableRow hover selected={editModeRows.includes(row.StudentCardCode)} tabIndex={-1} key={row.PupilID} sx={{ cursor: "pointer" }}>
                                        <TableCell align="center">
                                            <IconButton onClick={() => toggleEditMode(row.StudentCardCode)}>
                                                <EditIcon fontSize="small" color={editModeRows.includes(row.StudentCardCode) ? "primary" : "action"} />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell align="center">{row.PupilID}</TableCell>
                                        <TableCell align="center">{`${row.Pupil_FirstName} ${row.Pupil_LastName}`}</TableCell>
                                        <TableCell align="center">{permanentInfo.childClasses?.[row.Class] || "-"}</TableCell>

                                        {/* Editable Phone1 */}
                                        <TableCell align="center">
                                            {editModeRows.includes(row.StudentCardCode) ? (
                                                <TextField
                                                    variant="standard"
                                                    value={getLiveFieldValue(row.StudentCardCode, "Phone1", row.Phone1 || "")}
                                                    onChange={(e) => handleEdit(row.StudentCardCode, "Phone1", e.target.value)}
                                                    inputProps={{ style: { textAlign: 'center' } }}
                                                    error={
                                                        editModeRows.includes(row.StudentCardCode) &&
                                                        !!getLiveFieldValue(row.StudentCardCode, "Phone1", row.Phone1) &&
                                                        !isValidIsraeliPhone(getLiveFieldValue(row.StudentCardCode, "Phone1", row.Phone1))
                                                    }
                                                    helperText={
                                                        editModeRows.includes(row.StudentCardCode) &&
                                                            !!getLiveFieldValue(row.StudentCardCode, "Phone1", row.Phone1) &&
                                                            !isValidIsraeliPhone(getLiveFieldValue(row.StudentCardCode, "Phone1", row.Phone1))
                                                            ? t("insertValidPhone")
                                                            : ""
                                                    }
                                                />
                                            ) : (
                                                row.Phone1 || "-"
                                            )}
                                        </TableCell>

                                        {/* Editable Role */}
                                        <TableCell align="center">
                                            {editModeRows.includes(row.StudentCardCode) ? (
                                                <>
                                                    <Autocomplete
                                                        options={pupilRolesList}
                                                        fullWidth
                                                        sx={{ mr: '0.9rem' }}
                                                        renderOption={(props, option) => (
                                                            <Box component="li" {...props} key={option.RoleCode}>
                                                                {option.RoleName}
                                                            </Box>
                                                        )}
                                                        isOptionEqualToValue={(option, value) =>
                                                            option?.RoleCode === value?.RoleCode
                                                        }
                                                        getOptionLabel={(option) => option?.RoleName || ''}

                                                        value={getLiveFieldValue(row.StudentCardCode, "PupilRole", row.Role)}

                                                        onChange={(e, value) => {
                                                            handleEdit(row.StudentCardCode, "PupilRole", value || null);
                                                            if (value?.RoleCode !== "90") {
                                                                setCustomRoles(prev => {
                                                                    const copy = { ...prev };
                                                                    delete copy[row.StudentCardCode];
                                                                    return copy;
                                                                });
                                                            }
                                                        }}

                                                        noOptionsText={t("roleNotFound")}
                                                        renderInput={(params) => (
                                                            <TextField {...params} variant="standard" fullWidth />
                                                        )}
                                                    />

                                                    {getLiveFieldValue(row.StudentCardCode, "PupilRole", row.Role)?.RoleCode === "90" && (
                                                        <TextField
                                                            width="10rem"
                                                            variant="standard"
                                                            placeholder={t("insertCustomRole")}
                                                            value={customRolesText[row.StudentCardCode] || ""}
                                                            onChange={(e) => {
                                                                const value = e.target.value.slice(0, 15);
                                                                setCustomRolesText(prev => ({ ...prev, [row.StudentCardCode]: value }));
                                                                setCustomRolesArray(prev => {
                                                                    const updated = prev.filter(entry => entry.StudentCardCode !== row.StudentCardCode);
                                                                    return [...updated, { StudentCardCode: row.StudentCardCode, CustomRoleName: value }];
                                                                });
                                                                handleEdit(row.StudentCardCode, "PupilRole", {
                                                                    RoleCode: "90",
                                                                    RoleName: value
                                                                });
                                                            }}
                                                            inputProps={{ maxLength: 15, style: { textAlign: 'center' } }}
                                                            helperText={`${(customRolesText[row.StudentCardCode] || "").length}/15`}
                                                            fullWidth
                                                        />
                                                    )}
                                                </>
                                            ) : (
                                                (() => {
                                                    const val = getLiveFieldValue(row.StudentCardCode, "PupilRole", row.Role);
                                                    return val?.RoleName || t("noRole");
                                                })()
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[15, 30, 50]}
                    component="div"
                    count={visibleRows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={t("rowsPerPage")}
                />
            </Paper>
        </Box>
    );
}

PupilsRolesTable.propTypes = {
    rows: PropTypes.arrayOf(
        PropTypes.shape({
            PupilID: PropTypes.string.isRequired,
            Pupil_FirstName: PropTypes.string,
            Pupil_LastName: PropTypes.string,
            Class: PropTypes.string,
            Phone: PropTypes.string,
            Role: PropTypes.string
        })
    ),
};